import * as React from 'react';
import { Field, Form, Formik } from 'formik';
import * as Yup from 'yup';
import { DatePicker as SitDatePicker } from 'Src/components/DatePicker/DatePicker';
import { TimePicker } from 'Src/components/TimePicker/TimePicker';
import propz from 'propz';
import {
  getName,
  getNameByPlaceOrPostcode,
  searchFunctionEventParticipants
} from 'Src/helpers/autocomplete/autocomplete';
import { Autocomplete } from 'Src/components/Autocomplete/Autocomplete';
import { AppUser } from 'Src/views/App/App';
import { EVENT_TYPES, EVENT_TYPES_SERVER_TO_CLIENT_MAPPING } from 'Src/consts/event';
import { Map } from 'Src/components/Map/Map';
import { Point } from 'Src/models/venue';
import { VENUE_DISTANCE, VENUE_DISTANCE_SERVER_TO_CLIENT_MAPPING } from 'Src/consts/venue';
import { createVenue } from 'Src/helpers/service/admin/venues';
import { SimpleModal } from 'Src/components/SimpleModal/SimpleModal';
import { HelpModal } from 'Src/components/HelpModal/HelpModal';
import { HelpIconButton } from 'Src/components/HelpIconButton/HelpIconButton';
import { VenueForm } from 'Src/views/GenericView/AdminView/School/Venues/VenueForm/VenueForm';
import { SchoolForm } from 'Src/models/form';
import {
  getAgesForMultiSelect,
  getAgesFromMultiSelect,
  getGendersForMultiSelect,
  getGendersFromMultiSelect
} from 'Src/helpers/multiselect/multiselect';
import { Switch } from 'Src/components/Switch/Switch';
import { convertPlaceToPostcodeWithPlaceName, getDefaultPoint, getPostcodes, isPlace } from 'Src/helpers/venue/venue';
import './SchoolEventCopyForm.scss';
import '../SchoolEventsForm/SchoolEventForm.scss';
import { SchoolEvent } from 'Src/models/event';
import { EventDetail } from 'Src/models/eventDetail';
import { isClubEvent } from 'Src/helpers/event/event';

const AGE_KEY_STAGES = [
  { label: 'EYFS', names: ['Reception'] },
  { label: 'KS1', names: ['Y1', 'Y2'] },
  { label: 'KS2', names: ['Y3', 'Y4', 'Y5', 'Y6'] },
  { label: 'KS3', names: ['Y7', 'Y8', 'Y9'] },
  { label: 'KS4', names: ['Y10', 'Y11'] },
  { label: 'KS5', names: ['Y12', 'Y13'] }
];

function getAgeKeyStageGroups(ages: any[]) {
  const indexedAges = (ages || []).map((item, index) => ({ item, index }));
  const assigned = new Set<number>();
  const groups = AGE_KEY_STAGES.map(stage => ({
    label: stage.label,
    items: indexedAges.filter(({ item, index }) => {
      const name = String(item.name || '').replace(/\s+/g, '');
      const isMatch = stage.names.some(stageName => stageName.toLowerCase() === name.toLowerCase());
      if (isMatch) assigned.add(index);
      return isMatch;
    })
  })).filter(group => group.items.length > 0);
  const otherItems = indexedAges.filter(({ index }) => !assigned.has(index));
  return otherItems.length ? [...groups, { label: 'Other', items: otherItems }] : groups;
}
import { getAllHouses } from 'Src/helpers/service/admin/houses';
import { SchoolHouse } from 'Src/models/house';

interface Props {
  schoolEvent: SchoolEvent;
  eventDetails: EventDetail;
  participants: any[];
  houses: any[];
  forms: SchoolForm[];
  onSubmit: (data: any) => void;
  onCancel: () => void;
  user: AppUser;
}

interface State {
  participants: any[];
  point: Point;
  allHouses: SchoolHouse[];
  venue: any;
  houses: any[];
  eventType: string;
  distance: number;
  isDeleteVenueModalOpen: boolean;
  isCreateVenueFormOpen: boolean;
  multiparty: boolean;
  isBasicDetailsHelpOpen: boolean;
}

export class SchoolEventCopyForm extends React.Component<Props, State> {
  private _isMounted: boolean = false;

  constructor(props) {
    super(props);

    const { schoolEvent, user, participants } = this.props;
    const venue = propz.get(schoolEvent, ['venue']);
    const participantsSchools = participants
      .map(participant => participant.school)
      .filter(
        participantsSchool => typeof participantsSchool !== 'undefined' && participantsSchool.id !== user.activeSchoolId
      );

    this.state = {
      houses: propz.get(this.props, ['houses'], []),
      allHouses: [],
      participants: participantsSchools,
      point: propz.get(venue, ['point'], getDefaultPoint(user)),
      distance: VENUE_DISTANCE.UNLIMITED,
      eventType: propz.get(schoolEvent, ['eventType'], EVENT_TYPES.EXTERNAL_SCHOOLS),
      venue: propz.get(schoolEvent, ['venue'], undefined),
      isDeleteVenueModalOpen: false,
      isCreateVenueFormOpen: false,
      multiparty: propz.get(schoolEvent, ['multiparty'], false),
      isBasicDetailsHelpOpen: false
    };
  }

  componentDidMount() {
    this._isMounted = true;

    const { user } = this.props;
    getAllHouses(user).then(allHouses => {
      if (this._isMounted) {
        this.setState({ allHouses });
      }
    });
  }

  componentWillUnmount() {
    this._isMounted = false;
  }

  applyDatePart = (value: Date, datePart: Date): Date =>
    new Date(
      datePart.getFullYear(),
      datePart.getMonth(),
      datePart.getDate(),
      value.getHours(),
      value.getMinutes(),
      0,
      0
    );

  applyTimePart = (value: Date, timePart: Date): Date =>
    new Date(value.getFullYear(), value.getMonth(), value.getDate(), timePart.getHours(), timePart.getMinutes(), 0, 0);

  addOneHour = (value: Date): Date => {
    const nextValue = new Date(value.getTime());
    nextValue.setHours(nextValue.getHours() + 1, nextValue.getMinutes(), 0, 0);
    return nextValue;
  };

  getEventSchema() {
    return Yup.object().shape({
      startTime: Yup.date().test('startTime', 'Must be earlier than end time', function(value) {
        const { endTime } = this.parent;
        if (!value || !endTime) return true;
        return value <= endTime;
      }),
      endTime: Yup.date().test('endTime', 'Must be later than start time', function(value) {
        const { startTime } = this.parent;
        if (!value || !startTime) return true;
        return value >= startTime;
      }),
      replyToEmail: Yup.string()
        .nullable()
        .email('Invalid email format')
    });
  }

  getNewPoint = pointNew => {
    this.setState({ point: pointNew });
  };

  onCancelCreateVenueFormOpenClick = (): void => {
    this.setState({ isCreateVenueFormOpen: false });
  };

  createVenueFormOpen = (): void => {
    this.setState({ isCreateVenueFormOpen: true });
  };

  onSubmitVenueForm = (data): void => {
    const { user } = this.props;

    createVenue(user, data).then(venue => {
      const convertPlace = convertPlaceToPostcodeWithPlaceName(venue);
      this.setState({
        venue: convertPlace,
        point: convertPlace.point,
        isCreateVenueFormOpen: false
      });
    });
  };

  renderCreateVenueForm() {
    const { user } = this.props;
    return (
      <SimpleModal isOpen={this.state.isCreateVenueFormOpen} title={'New venue'}>
        <VenueForm user={user} onCancel={this.onCancelCreateVenueFormOpenClick} onSubmit={this.onSubmitVenueForm} />
      </SimpleModal>
    );
  }

  renderBasicDetailsHelpModal() {
    return (
      <HelpModal
        isOpen={this.state.isBasicDetailsHelpOpen}
        title="Copy event"
        badgeText="Event copy"
        subtitle="When copying an event, you can change its date, time, ages, venue and other available details before creating the duplicate."
        onClose={() => this.setState({ isBasicDetailsHelpOpen: false })}
      >
        <div className="row">
          <div className="col-md-6 mb-3">
            <div className="border rounded p-3 h-100">
              <div className="font-weight-bold mb-2">Start and finish time</div>
              <div className="text-muted">
                Set the date and time for the copied event. By default the original event times are pre-filled.
              </div>
            </div>
          </div>

          <div className="col-md-6 mb-3">
            <div className="border rounded p-3 h-100">
              <div className="font-weight-bold mb-2">Short description</div>
              <div className="text-muted">Optionally change the short description for the copied event.</div>
            </div>
          </div>

          <div className="col-md-6 mb-3">
            <div className="border rounded p-3 h-100">
              <div className="font-weight-bold mb-2">Opponent schools</div>
              <div className="text-muted">
                For inter-school events you can change the opponent schools. Sport and gender remain the same as the
                original event; age groups can be adjusted in the Audience section.
              </div>
            </div>
          </div>

          <div className="col-md-6 mb-3">
            <div className="border rounded p-3 h-100">
              <div className="font-weight-bold mb-2">Venue</div>
              <div className="text-muted">
                You can select a different venue for the copied event or keep the original one.
              </div>
            </div>
          </div>

          <div className="col-md-6 mb-3">
            <div className="border rounded p-3 h-100">
              <div className="font-weight-bold mb-2">Reply-to email address</div>
              <div className="text-muted">Optionally set a different reply-to email address for the copied event.</div>
            </div>
          </div>
        </div>
      </HelpModal>
    );
  }

  renderParticipants(participants, setFieldValue) {
    return participants.map(participant => {
      const imageSrc =
        participant.pic && String(participant.pic).startsWith('//') ? `https:${participant.pic}` : participant.pic;

      return (
        <div key={participant.id} className="eEntityChipWrap">
          <div className="eEntityChip">
            <div className="eEntityChip__avatar">
              {imageSrc ? (
                <img src={imageSrc} alt={participant.name} className="eEntityChip__avatarImg" />
              ) : (
                <div className="eEntityChip__avatarFallback">
                  {String(participant.name || '?')
                    .trim()
                    .charAt(0)
                    .toUpperCase()}
                </div>
              )}
            </div>
            <div className="eEntityChip__name" title={participant.name}>
              {participant.name}
            </div>
            <button
              type="button"
              className="eEntityChip__remove"
              onClick={() => {
                const participantsFiltered = participants.filter(p => p.id !== participant.id);
                setFieldValue('participants', participantsFiltered);
                this.setState({ participants: participantsFiltered });
              }}
            >
              ×
            </button>
          </div>
        </div>
      );
    });
  }

  getSchools = (text: string) => {
    const { user } = this.props;
    const { participants, distance } = this.state;
    return searchFunctionEventParticipants(text, user, participants, distance);
  };

  onAgeChange = (index: number, setFieldValue, values) => {
    setFieldValue(
      'ages',
      (values.ages || []).map((item, itemIndex) =>
        itemIndex === index ? { ...item, isChecked: !item.isChecked } : item
      )
    );
  };

  render() {
    const { participants, point, venue, houses, eventType, distance, multiparty } = this.state;
    const { schoolEvent, user, forms, eventDetails } = this.props;
    const { activeSchool } = user;
    const isExistActiveSchoolPostcode = propz.get(user, ['activeSchool', 'postcode'], false);

    const initialAges = getAgesForMultiSelect(activeSchool, forms, schoolEvent.ages);
    const eventInitial = {
      startTime: new Date(schoolEvent.startTime),
      endTime: new Date(schoolEvent.endTime),
      sport: schoolEvent.sport,
      sportId: schoolEvent.sportId,
      ages:
        Array.isArray(schoolEvent.ages) && schoolEvent.ages.length > 0
          ? initialAges
          : initialAges.map(item => ({ ...item, isChecked: true })),
      gender: getGendersForMultiSelect(schoolEvent.sport, schoolEvent.gender),
      eventType,
      participants,
      venue,
      houses,
      distance,
      multiparty: schoolEvent.multiparty,
      shortDescription: propz.get(eventDetails, ['shortDescription'], ''),
      replyToEmail: schoolEvent.replyToEmail || ''
    };

    return (
      <div className="eSchoolEventForm eSchoolEventCopyForm">
        {this.renderCreateVenueForm()}
        {this.renderBasicDetailsHelpModal()}

        <div className="eSchoolEventForm__shell">
          <div className="eSchoolEventForm__content">
            <Formik
              initialValues={eventInitial}
              validationSchema={this.getEventSchema()}
              onSubmit={values => {
                const { gender, ages, ...rest } = values;
                const dataToSubmit = {
                  ...rest,
                  venue: this.state.venue,
                  gender: getGendersFromMultiSelect(gender),
                  ages: getAgesFromMultiSelect(activeSchool, forms, ages)
                };
                this.props.onSubmit(dataToSubmit);
              }}
            >
              {({ touched, errors, setFieldValue, values }) => (
                <Form>
                  {/* BASIC DETAILS */}
                  <section className="eSchoolEventForm__section eSchoolEventForm__sectionBasicDetails">
                    <div className="eSchoolEventForm__sectionHeader">
                      <div className="eSchoolEventForm__sectionTitleRow">
                        <h2 className="eSchoolEventForm__sectionTitle">Basic details</h2>
                        <HelpIconButton
                          onClick={() => this.setState({ isBasicDetailsHelpOpen: true })}
                          label="How copying an event works"
                        />
                      </div>
                    </div>

                    <div className="eSchoolEventForm__basicCompactLayout">
                      <div className="eSchoolEventForm__basicTopRow">
                        <div className="eSchoolEventForm__basicDateField eSchoolEventForm__basicDateFieldDate">
                          <Field name="startTime">
                            {({ field }) => (
                              <SitDatePicker
                                label="Date"
                                value={field.value}
                                onChange={(date: Date | null) => {
                                  if (!date) return;
                                  setFieldValue('startTime', this.applyDatePart(values.startTime, date));
                                  setFieldValue(
                                    'endTime',
                                    this.applyDatePart(values.endTime || this.addOneHour(values.startTime), date)
                                  );
                                }}
                                format="DD MMM YYYY"
                                required
                                clearable={false}
                                className="eSchoolEventForm__datePicker"
                              />
                            )}
                          </Field>
                        </div>

                        <div className="eSchoolEventForm__basicDateField eSchoolEventForm__basicDateFieldStart">
                          <Field name="startTime">
                            {({ field }) => (
                              <TimePicker
                                label="Start time"
                                value={field.value}
                                onChange={(time: Date | null) => {
                                  if (!time) return;
                                  const nextStartTime = this.applyTimePart(values.startTime, time);
                                  setFieldValue('startTime', nextStartTime);
                                  setFieldValue('endTime', this.addOneHour(nextStartTime));
                                }}
                                minuteStep={5}
                                required
                                showConfirm
                                clearable={false}
                                className="eSchoolEventForm__timePicker"
                              />
                            )}
                          </Field>
                        </div>

                        <div className="eSchoolEventForm__basicDateField eSchoolEventForm__basicDateFieldEnd">
                          <Field name="endTime">
                            {({ field }) => (
                              <TimePicker
                                label="Finish / collection"
                                value={field.value}
                                onChange={(time: Date | null) => {
                                  if (!time) return;
                                  setFieldValue(
                                    'endTime',
                                    this.applyTimePart(values.endTime || values.startTime, time)
                                  );
                                }}
                                minuteStep={5}
                                required
                                showConfirm
                                clearable={false}
                                className="eSchoolEventForm__timePicker"
                                error={touched.endTime && errors.endTime ? String(errors.endTime) : undefined}
                              />
                            )}
                          </Field>
                        </div>
                      </div>

                      {!isClubEvent(schoolEvent) && (
                        <div className="eSchoolEventForm__basicField">
                          <label className="eSchoolEventForm__label">Short description</label>
                          <Field type="text" name="shortDescription" className="form-control" />
                        </div>
                      )}

                      <div className="eSchoolEventForm__sportRow">
                        <div className="eSchoolEventForm__sportMain">
                          <div className="eSchoolEventForm__sportHeader">
                            <label className="eSchoolEventForm__label mb-0">Activity / Sport</label>
                          </div>
                          <div className="form-control" style={{ background: '#f5f7fa', color: '#607181' }}>
                            {propz.get(values, ['sport', 'name'], '—')}
                          </div>
                        </div>

                        <div className="eSchoolEventForm__sportToggles">
                          <div className="eSchoolEventForm__inlineOption">
                            <div className="eSchoolEventForm__inlineOptionText">Multiple teams available</div>
                            <Switch
                              name="multiparty"
                              text=""
                              value={multiparty}
                              customClass="mb-0"
                              disabled={true}
                              onChange={() => {}}
                            />
                          </div>
                        </div>
                      </div>

                      <div className="eSchoolEventForm__audienceCard eSchoolEventCopyForm__audienceCard">
                        <div className="eSchoolEventForm__audienceHeader">
                          <span className="eSchoolEventForm__audienceIcon" aria-hidden="true">
                            <svg viewBox="0 0 24 24">
                              <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
                              <circle cx="9" cy="7" r="4" />
                              <path d="M22 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
                            </svg>
                          </span>
                          <div className="eSchoolEventForm__audienceTitleBlock">
                            <div className="eSchoolEventForm__audienceTitle">Audience</div>
                            <div className="eSchoolEventForm__audienceDescription">
                              Carried over from the original event.
                            </div>
                          </div>
                        </div>
                        <div className="eSchoolEventForm__audienceDivider" />
                        <div className="eSchoolEventForm__basicAudienceRow">
                          <div className="eSchoolEventForm__basicAudienceItem eSchoolEventForm__basicAudienceItemGender">
                            <label className="eSchoolEventForm__label">Gender</label>
                            <div className="eSchoolEventForm__chipGroup">
                              {(values.gender || [])
                                .filter(item => item.isChecked)
                                .map((item, index) => (
                                  <span
                                    key={`${item.name}-${index}`}
                                    className="eSchoolEventForm__audienceChip is-selected"
                                  >
                                    {item.name}
                                  </span>
                                ))}
                            </div>
                          </div>

                          <div className="eSchoolEventForm__basicAudienceItem eSchoolEventForm__basicAudienceItemAge">
                            <label className="eSchoolEventForm__label">Age groups</label>
                            <div className="eSchoolEventForm__ageStageList">
                              {getAgeKeyStageGroups(values.ages || []).map(group => (
                                <div key={group.label} className="eSchoolEventForm__ageStage">
                                  <div className="eSchoolEventForm__ageStageLabel">{group.label}</div>
                                  <div className="eSchoolEventForm__chipGroup eSchoolEventForm__chipGroupAges">
                                    {group.items.map(({ item, index }) => (
                                      <button
                                        key={String(item.age || item.name || index)}
                                        type="button"
                                        className={`eSchoolEventForm__audienceChip ${
                                          item.isChecked ? 'is-selected' : ''
                                        }`}
                                        onClick={() => this.onAgeChange(index, setFieldValue, values)}
                                      >
                                        {item.name || item.age}
                                      </button>
                                    ))}
                                  </div>
                                </div>
                              ))}
                            </div>
                            <div className="eSchoolEventForm__fieldHint">
                              All year groups are selected when the original event is open to all ages.
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </section>

                  {/* EVENT SETUP & PARTICIPANTS */}
                  <section className="eSchoolEventForm__section">
                    <div className="eSchoolEventForm__sectionHeader">
                      <h2 className="eSchoolEventForm__sectionTitle">Event setup & participants</h2>
                    </div>

                    <div className="container-fluid mb-3">
                      <div className="row">
                        <div className="col-md-3">
                          <div className="form-group">
                            <label className="eSchoolEventForm__label">Event type</label>
                            <div className="form-control" style={{ background: '#f5f7fa', color: '#607181' }}>
                              {EVENT_TYPES_SERVER_TO_CLIENT_MAPPING[eventType] || eventType}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="container-fluid">
                      <div className="row">
                        {eventType === EVENT_TYPES.EXTERNAL_SCHOOLS && (
                          <div className="col-md-6">
                            <div className="form-group">
                              <label className="eSchoolEventForm__label">Opponent schools</label>
                              <div className="eSchoolEventForm__fieldHint mb-2">
                                Carried over from the original event — add or remove as needed.
                              </div>
                              <Field name="participants">
                                {() => (
                                  <Autocomplete
                                    searchFunction={this.getSchools}
                                    getElementTitle={getName}
                                    customClass="mFullWidth mb-3"
                                    defaultItem={undefined}
                                    placeholder="Start typing the school name"
                                    isItemWithImage={true}
                                    disabled={multiparty ? false : values.participants.length > 0}
                                    onSelect={participant => {
                                      const nextParticipants = [...values.participants, participant];
                                      setFieldValue('participants', nextParticipants);
                                      this.setState({ participants: nextParticipants });
                                    }}
                                  />
                                )}
                              </Field>
                              <div className="eEntityStrip">
                                {this.renderParticipants(values.participants, setFieldValue)}
                              </div>
                            </div>
                          </div>
                        )}

                        {eventType === EVENT_TYPES.EXTERNAL_SCHOOLS && isExistActiveSchoolPostcode && (
                          <div className="col-md-3">
                            <div className="form-group">
                              <label className="eSchoolEventForm__label">Venue distance</label>
                              <div
                                className="eSchoolEventForm__fieldHint mb-2 eSchoolEventCopyForm__alignmentHint"
                                aria-hidden="true"
                              >
                                Distance filter
                              </div>
                              <Field
                                component="select"
                                name="distance"
                                className="form-control eSchoolEventForm__eventSelect"
                                onChange={event => {
                                  const nextDistance = Number.parseInt(event.target.value);
                                  setFieldValue('distance', nextDistance);
                                  this.setState({ distance: nextDistance });
                                }}
                              >
                                <option value={VENUE_DISTANCE.UNLIMITED}>
                                  {VENUE_DISTANCE_SERVER_TO_CLIENT_MAPPING.UNLIMITED}
                                </option>
                                <option value={VENUE_DISTANCE['10M']}>
                                  {VENUE_DISTANCE_SERVER_TO_CLIENT_MAPPING['10M']}
                                </option>
                                <option value={VENUE_DISTANCE['20M']}>
                                  {VENUE_DISTANCE_SERVER_TO_CLIENT_MAPPING['20M']}
                                </option>
                                <option value={VENUE_DISTANCE['30M']}>
                                  {VENUE_DISTANCE_SERVER_TO_CLIENT_MAPPING['30M']}
                                </option>
                                <option value={VENUE_DISTANCE['40M']}>
                                  {VENUE_DISTANCE_SERVER_TO_CLIENT_MAPPING['40M']}
                                </option>
                                <option value={VENUE_DISTANCE['50M']}>
                                  {VENUE_DISTANCE_SERVER_TO_CLIENT_MAPPING['50M']}
                                </option>
                              </Field>
                            </div>
                          </div>
                        )}

                        {eventType === EVENT_TYPES.INTERNAL_HOUSES && (
                          <div className="col-md-6">
                            <div className="form-group">
                              <label className="eSchoolEventForm__label">Houses</label>
                              <div className="eEntityStrip">
                                {values.houses.map(house => (
                                  <div key={house.id} className="eEntityChipWrap">
                                    <div className="eEntityChip">
                                      <div className="eEntityChip__name">{house.name}</div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          </div>
                        )}

                        {eventType === EVENT_TYPES.INTERNAL_TEAMS && (
                          <div className="col-md-6">
                            <div className="form-group">
                              <label className="eSchoolEventForm__label">Internal teams event</label>
                              <div className="form-control" style={{ background: '#f5f7fa', color: '#607181' }}>
                                Participants will be carried over from the original event
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </section>

                  {/* VENUE & CONTACT */}
                  <section className="eSchoolEventForm__section">
                    <div className="eSchoolEventForm__sectionHeader">
                      <h2 className="eSchoolEventForm__sectionTitle">Venue & contact</h2>
                    </div>

                    <div className="container-fluid">
                      <div className="row">
                        <div className="col-md-8">
                          <div className="form-group">
                            <label className="eSchoolEventForm__label">Venue</label>
                            <div className="eSchoolEventForm__venueRow">
                              <div className="eSchoolEventForm__venueInput">
                                <Field name="venue">
                                  {() => (
                                    <Autocomplete
                                      searchFunction={text =>
                                        getPostcodes(
                                          text,
                                          user,
                                          values.eventType === EVENT_TYPES.EXTERNAL_SCHOOLS,
                                          this.state.participants
                                        )
                                      }
                                      getElementTitle={getNameByPlaceOrPostcode}
                                      customClass="mFullWidth mb-0"
                                      placeholder="Start typing the venue name"
                                      defaultItem={this.state.venue}
                                      disabled={
                                        values.eventType === EVENT_TYPES.EXTERNAL_SCHOOLS &&
                                        values.participants.length === 0
                                      }
                                      onSelect={venueItem => {
                                        let postcode = venueItem;
                                        if (isPlace(postcode)) {
                                          postcode = convertPlaceToPostcodeWithPlaceName(postcode);
                                        }
                                        setFieldValue('venue', postcode);
                                        this.setState({ venue: postcode });
                                        this.getNewPoint(postcode.point);
                                      }}
                                    />
                                  )}
                                </Field>
                              </div>
                              <button
                                type="button"
                                className="btn btn-outline-secondary eSchoolEventForm__venueBtn"
                                onClick={this.createVenueFormOpen}
                                disabled={
                                  values.eventType === EVENT_TYPES.EXTERNAL_SCHOOLS && values.participants.length === 0
                                }
                              >
                                Create venue
                              </button>
                            </div>
                            {touched.venue && errors.venue ? (
                              <div className="alert alert-danger mt-2">{String(errors.venue)}</div>
                            ) : null}
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="container-fluid">
                      <div className="row">
                        <div className="col-md-5">
                          <div className="form-group">
                            {propz.get(values.venue, ['venueType']) !== 'TBD' && typeof point !== 'undefined' && (
                              <Map
                                key={values.venue ? values.venue.id : 'emptyPostcode'}
                                point={point}
                                getNewPoint={this.getNewPoint}
                                customStylingClass="eSchoolEventVenueMap"
                                isMarkerDraggable={true}
                              />
                            )}
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="container-fluid">
                      <div className="row">
                        <div className="col-md-6">
                          <div className="form-group">
                            <label className="eSchoolEventForm__label">Reply-to email address</label>
                            <Field
                              type="email"
                              name="replyToEmail"
                              className="form-control"
                              placeholder="Enter reply-to email address"
                              value={values.replyToEmail || ''}
                            />
                            {touched.replyToEmail && errors.replyToEmail && (
                              <div className="alert alert-danger">{errors.replyToEmail}</div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </section>

                  {/* ACTIONS */}
                  <div className="eSchoolEventForm__actions">
                    <div className="eSchoolEventForm__actionsInfo">
                      <div className="eSchoolEventForm__actionsTitle">Ready to duplicate?</div>
                      <div className="eSchoolEventForm__actionsText">
                        Review the details above and create a separate event from this copy.
                      </div>
                    </div>
                    <div className="eSchoolEventForm__actionsButtons">
                      <button type="button" className="eSchoolEventForm__btnCancel" onClick={this.props.onCancel}>
                        Cancel
                      </button>
                      <button type="submit" className="eSchoolEventForm__btnPrimary eSchoolEventForm__btnNext">
                        <svg viewBox="0 0 24 24" aria-hidden="true">
                          <rect x="8" y="8" width="12" height="12" rx="2" />
                          <path d="M16 8V6a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2h2" />
                        </svg>
                        Duplicate event
                      </button>
                    </div>
                  </div>
                </Form>
              )}
            </Formik>
          </div>
        </div>
      </div>
    );
  }
}
